import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { TrendingUp } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import { fetchAssessmentHistory } from "../lib/api";

export const ReadingChart = ({ cadet }) => {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchAssessmentHistory().then(setHistory).catch(() => setHistory([]));
  }, [cadet?.reading_level]);

  const data = history.map((h, i) => ({
    idx: i + 1,
    level: Number(h.level.toFixed(1)),
    date: new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: `${h.score}/${h.total}`,
  }));

  const first = data[0]?.level ?? cadet?.reading_level ?? 1.0;
  const latest = data[data.length - 1]?.level ?? cadet?.reading_level ?? 1.0;
  const delta = +(latest - first).toFixed(1);
  const trending = delta >= 0;

  return (
    <div className="eigoo-glass rounded-3xl p-6" data-testid="reading-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center eigoo-glow-gold">
            <TrendingUp className="text-black" size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-yellow-300/80 font-display">Reading Trajectory</div>
            <div className="font-display font-bold text-xl">Level Over Time</div>
          </div>
          <SpeakerButton
            text={`You started at level ${first.toFixed(1)} and you are now at level ${latest.toFixed(1)}. ${trending && delta !== 0 ? `That is up by ${Math.abs(delta).toFixed(1)}. Amazing work, Commander!` : "Keep taking drills to track your growth!"}`}
            testid="speaker-reading-chart"
            size="sm"
          />
        </div>
        {data.length > 1 && (
          <div className={`px-3 py-1 rounded-full text-xs font-display font-bold ${trending ? "bg-green-500/15 text-green-300 border border-green-500/30" : "bg-red-500/15 text-red-300 border border-red-500/30"}`}>
            {trending ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}
          </div>
        )}
      </div>

      {data.length === 0 ? (
        <div className="text-center py-8 text-white/50 text-sm">
          Take a Reading Drill to start tracking progress.
        </div>
      ) : data.length === 1 ? (
        <div className="rounded-2xl bg-black/40 border border-yellow-300/20 p-5 text-center">
          <div className="text-xs uppercase tracking-widest text-white/50 font-display mb-1">Baseline</div>
          <div className="font-display font-black text-4xl text-yellow-200">{data[0].level.toFixed(1)}</div>
          <div className="text-xs text-white/50 mt-1">Take another drill to see your trajectory.</div>
        </div>
      ) : (
        <div className="w-full h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: -18 }}>
              <defs>
                <linearGradient id="goldLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FFC107" />
                  <stop offset="100%" stopColor="#FFD700" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,215,0,0.08)" strokeDasharray="3 3" />
              <XAxis dataKey="idx" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} domain={[0.5, 5]} />
              <Tooltip
                contentStyle={{ background: "rgba(10,10,10,0.95)", border: "1px solid rgba(255,215,0,0.35)", borderRadius: 12, color: "#fff" }}
                labelStyle={{ color: "#FFD700", fontWeight: 700 }}
                formatter={(value) => [value, "Level"]}
                labelFormatter={(i) => `Drill #${i} · ${data[i - 1]?.date}`}
              />
              <ReferenceLine y={first} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4" label={{ value: "start", fill: "rgba(255,255,255,0.4)", fontSize: 10, position: "insideTopLeft" }} />
              <Line type="monotone" dataKey="level" stroke="url(#goldLine)" strokeWidth={3} dot={{ fill: "#FFD700", stroke: "#1a1100", strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.length > 0 && (
        <div className="flex items-center justify-between mt-3 text-xs text-white/50">
          <span>Start: <span className="text-white/80 font-bold">{first.toFixed(1)}</span></span>
          <span>{data.length} drill{data.length !== 1 ? "s" : ""}</span>
          <span>Now: <span className="text-yellow-200 font-bold">{latest.toFixed(1)}</span></span>
        </div>
      )}
    </div>
  );
};

export default ReadingChart;
